import React, { useRef, useState, useLayoutEffect } from "react"
import useComponentSize from "@rehooks/component-size"
import { PostListItemStyles, PostListItemExpandedStyles } from "./constants"
import FreeTextFormat from "../shared/FreeTextFormat"

const MAX_CHARS = 250
const MIN_FONT_SIZE = 14
const HORIZONTAL_PADDING = 32 // px-4 on both sides
// leading-none (line-height: 1) clips descenders (y, g, j, p) on bold text.
const LINE_HEIGHT = 1.15

// Largest font size we'll ever use, scaled to how wide the card is so short
// headlines don't balloon to absurd sizes on wide screens.
const maxFontSizeFor = (width: number): number =>
  Math.min(48, Math.max(width / 14, MIN_FONT_SIZE))

interface TextAsImageProps {
  text?: string
  expanded?: boolean
  className?: string
  onClick?: (e: React.MouseEvent) => void
}

export const TextAsImage: React.FC<TextAsImageProps> = ({
  text,
  expanded = false,
  className = "",
  onClick = () => {},
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const containerSize = useComponentSize(containerRef)
  const [fontSize, setFontSize] = useState(MIN_FONT_SIZE)

  const safeText = text || ""
  const headlineText =
    safeText.length > MAX_CHARS ? `${safeText.slice(0, MAX_CHARS)}…` : safeText

  const width = containerSize.width
  const height = width ? (width * 9) / 16 : 0
  const availableWidth = Math.max(width - HORIZONTAL_PADDING, 0)
  const maxFontSize = width ? maxFontSizeFor(width) : MIN_FONT_SIZE

  // Binary-search for the largest font size (within sane bounds) at which
  // the headline actually fits the fixed-aspect-ratio box, rather than
  // guessing from raw character count. Measured off-screen so short and
  // long headlines alike land at a readable, non-overflowing size.
  useLayoutEffect(() => {
    const measureEl = measureRef.current
    if (!measureEl || !availableWidth || !height) return

    measureEl.style.width = `${availableWidth}px`

    const fits = (size: number) => {
      measureEl.style.fontSize = `${size}px`
      return measureEl.scrollHeight <= height
    }

    const hi = Math.max(Math.ceil(maxFontSize), MIN_FONT_SIZE)
    let best = MIN_FONT_SIZE

    if (fits(hi)) {
      best = hi
    } else {
      let lo = MIN_FONT_SIZE
      let top = hi
      while (lo <= top) {
        const mid = Math.floor((lo + top) / 2)
        if (fits(mid)) {
          best = mid
          lo = mid + 1
        } else {
          top = mid - 1
        }
      }
    }

    setFontSize(best)
  }, [headlineText, availableWidth, height, maxFontSize])

  const contentDivStyles = expanded
    ? PostListItemExpandedStyles.contentDiv
    : PostListItemStyles.contentDiv

  return (
    <div
      ref={containerRef}
      style={{ height }}
      onClick={onClick}
      className={
        contentDivStyles +
        " text-as-image relative flex flex-row items-center overflow-hidden px-4 leading-none font-bold bg-black text-white w-full " +
        className
      }
    >
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          visibility: "hidden",
          top: 0,
          left: 0,
          lineHeight: LINE_HEIGHT,
          fontWeight: "bold",
          overflowWrap: "anywhere",
        }}
      >
        {headlineText}
      </div>
      <div
        style={{ fontSize, lineHeight: LINE_HEIGHT }}
        className="w-full break-words"
      >
        <FreeTextFormat
          text={headlineText}
          expandable={false}
          expanded={false}
          linkify={true}
        />
      </div>
    </div>
  )
}
